import axios from "axios";
import { useCallback, useContext, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useImmer } from "use-immer";
import { APP_BASE_URL, GENERATE_OTP } from "../../Common/EndPoints";
import { doLogin } from "../../Services/DataService";

import { ActiveAlarmsData } from "../Alarms/ActiveAlarms";
import { useDispatch } from "react-redux";
import {
  loginSuccess,
  setSelectedCustomer,
  setSelectedDivision,
  setSelectedDivisions,
  setSelectedZone,
  setSelectedZones,
} from "../../features/authentication/auth-slice";
import useFirebaseCloudMessaging from "../../hooks/fcm/useFirebaseCloudMessaging";
import bcrypt from "bcryptjs";
import { encryptForURL } from "./encryption-utils";

const initialState = {
  userName: "",
  password: "",
  userNameError: false,
  passwordError: false,
  errorMsg: "",
  loading: false,
  otpButtonLoading: false,
  secretkey: "",
};
const PHONE_NUMBER_VALIDATION = /^\d{10}$/;
const EMAIL_VALIDATION = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/g;

function findStationLocation(permissions, customerName) {
  for (const permission of permissions) {
    const zone = Object.keys(permission)[0];
    const divisions = permission[zone];

    for (const division of divisions) {
      const divisionKey = Object.keys(division)[0];
      const stations = division[divisionKey];

      if (stations.some((station) => station.customerName === customerName)) {
        return { zone, division: divisionKey };
      }
    }
  }
  return { zone: null, division: null };
}

export function useLogin() {
  const { refetchUserData } = useContext(ActiveAlarmsData);
  const [state, setState] = useImmer(initialState);
  const { registerUser } = useFirebaseCloudMessaging();
  const dispatch = useDispatch();

  const navigate = useNavigate();

  useEffect(() => {
    const refreshToken = localStorage.getItem("refresh_token");
    if (refreshToken !== null && refreshToken !== undefined && refreshToken !== "undefined") {
      navigate("/home");
    }
  }, []);

  const validateForm = () => {
    let _errorMsg = "";
    if (!state.userName || !state.password) _errorMsg = "Field must not be empty";
    if (inputType === null) _errorMsg = "Invalid Email or Mobile Number";
    if (!state.password && inputType !== null) _errorMsg = "Something went Wrong";
    setState((state) => {
      state.userNameError = !state.userName || inputType === null;
      state.passwordError = !state.password;
      state.errorMsg = _errorMsg;
    });
    if (!state.userName || !state.password) {
      return false;
    }
    return true;
  };

  const handleChange = useCallback((type, value) => {
    setState((state) => {
      state[type] = value;
      state[`${type}Error`] = !value.length;
      if (type === "userName") {
        state.otpButtonLoading = false;
        state.secretkey = "";
        state.errorMsg = "";
      }
    });
  }, []);

  function resetPassword() {
    setState((state) => {
      state.password = "";
    });
  }
  const inputType = useMemo(() => {
    // if (!state.userName || state.userName.length === 0) return null;
    if (state.userName.match(PHONE_NUMBER_VALIDATION)) {
      resetPassword();
      return "mobile";
    }
    if (state.userName.match(EMAIL_VALIDATION)) {
      resetPassword();
      return "email";
    }
    return null;
    // return "email";
  }, [state.userName]);

  const sendOTP = useCallback(() => {
    const params = { mobileNo: state.userName, otpTimeOut: 180 };
    setState((state) => {
      state.otpButtonLoading = true;
    });
    axios.post(`${APP_BASE_URL}${GENERATE_OTP}`, null, { params }).then(
      (res) => {
        setState((state) => {
          state.secretkey = res.data.data.secretkey;
        });
      },
      (err) => {
        setState((state) => {
          state.errorMsg = err?.response?.data?.error?.message ?? "Something went wrong! Please try again later.";
          state.otpButtonLoading = false;
        });
      },
    );
  }, [state.userName]);
  // Function to hash data
  // const hashData = async (data) => {
  //   // Generate a salt
  //   const salt = await bcrypt.genSalt(10);
  //   // Hash the data with the generated salt
  //   const hashedPassword = await bcrypt.hash(data, salt);
  //   const urlSafeHash = encodeURIComponent(hashedPassword);
  //   return urlSafeHash;
  // };

  const hashData = async (password) => {
    const saltRounds = -1;
    const hash = await bcrypt.hash(password, saltRounds);

    // Convert to Base64 URL-safe encoding
    // const urlSafeHash = btoa(hash).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

    return hash;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (validateForm()) {
      const user = {
        email: state.userName,
        password: state.password,
        secretkey: state.secretkey,
      };
      setState((state) => {
        state.loading = true;
        state.errorMsg = "";
      });
      doLogin(user, inputType).then(
        async (user_data) => {
          console.log("user_data", user_data);

          const userId = user_data?.user?.userId;
          const userEmail = user_data?.user?.email;
          if (!userId) {
            return;
          }
          const hashed_pass = await hashData(userEmail);
          const encrypted_creds = encryptForURL({ id: state.userName, value: state.password });
          localStorage.setItem("en_cr", encrypted_creds);

          localStorage.setItem("id_key", hashed_pass);
          const selectedStationZoneDivision = findStationLocation(
            user_data.user.permissions,
            user_data.user.selectedCustomer.customerName,
          );
          dispatch(
            setSelectedZones(
              Object.values(user_data.user.permissions).flatMap((permission) =>
                Object.keys(permission).map((item) => ({ id: item, value: item })),
              ),
            ),
          );
          dispatch(
            setSelectedDivisions(
              Object.values(user_data.user.permissions)
                .flatMap((permission) =>
                  Object.keys(permission).flatMap((zone) =>
                    permission[zone].map((division) => Object.keys(division)[0]),
                  ),
                )
                .map((item) => ({ id: item, value: item })),
            ),
          );
          dispatch(setSelectedCustomer(user_data.user.selectedCustomer));
          dispatch(
            setSelectedZone(
              selectedStationZoneDivision.zone
                ? { id: selectedStationZoneDivision.zone, value: selectedStationZoneDivision.zone }
                : null,
            ),
          );
          dispatch(
            setSelectedDivision(
              selectedStationZoneDivision.division
                ? { id: selectedStationZoneDivision.division, value: selectedStationZoneDivision.division }
                : null,
            ),
          );
          dispatch(loginSuccess(user_data));
          refetchUserData();
          registerUser(userId).then((res) => {
            console.log("res", res);
            navigate("/home");
          });

          //TODO: REMOVE THIS RELOAD!!!
          // window.location.reload();
        },
        (err) => {
          setState((state) => {
            state.errorMsg =
              "*" + err?.response?.data?.error?.message ?? "Something went wrong! Please try again later.";
            state.loading = false;
          });
        },
      );
    }
  };
  return useMemo(
    () => ({ state, setState, handleSubmit, handleChange, inputType, sendOTP }),
    [state, handleSubmit, handleChange, inputType],
  );
}
